import {ISymbolData, TheCryptSymbol, TheCryptSymbolStates} from "../symbols/TheCryptSymbol";
import {TheCrypt} from "../TheCrypt";
import {gsap, TimelineLite, TweenLite} from "gsap";
import {TheCryptGameConfig} from "../TheCryptGameConfig";
import {TheCryptSoundManager} from "../sound/TheCryptSoundManager";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";
import {TheCryptHelper} from "../utils/TheCryptHelper";
import {TheCryptFullWild} from "../symbols/TheCryptFullWild";
import {MathHelper} from "@nolimitcity/slot-game/bin/utils/MathHelper";
import {TimelineSprite} from "@nolimitcity/promo-panel/bin/utils/TimelineSprite";
import {ScatterCounter} from "../view/ScatterCounter";
import {TheCryptScatter} from "../symbols/TheCryptScatter";
import {TheCryptSoundConfig} from "../configs/TheCryptSoundConfig";

export class TheCryptReelView extends PIXI.Container {
    private readonly DURATION_SYMBOLS_FALL_OUT: number = 0.4;
    private readonly DURATION_SYMBOLS_FALL_IN: number = 0.3;
    private readonly DURATION_SYMBOLS_FALL: number = 0.3;
    private readonly DURATION_SYMBOLS_COLLAPSE_FALL: number = 0.1;
    private readonly DURATION_SYMBOLS_UNBLUR: number = 0.02;
    private readonly DURATION_SYMBOLS_EXPAND: number = 0.2;

    private readonly DURATION_FAST_SYMBOLS_FALL_IN: number = 0.2;
    private readonly DURATION_FAST_SYMBOLS_FALL_OUT: number = 0.4;
    private readonly DURATION_FAST_SYMBOLS_EXPAND: number = 0.2;
    //DURATION_FAST_SYMBOLS_COLLAPSE_FALL cant be 0
    private readonly DURATION_FAST_SYMBOLS_COLLAPSE_FALL: number = 0.05;
    private readonly DURATION_FAST_SYMBOLS_FALL_SPIN: number = 0.3;

    private readonly SYMBOLS_FALL_IN_DELAY_NEAR_WIN: number = 0.3;
    private readonly SYMBOLS_FALL_IN_BLUR_DELAY: number = 0.1;
    private readonly SYMBOLS_FALL_IN_DELAY: number = 0.05;
    private readonly SYMBOLS_FALL_OUT_DELAY: number = 0.08;
    private readonly START_DROP_IN_POSITION: number = -1000;

    private _fullWildSymbolView: PIXI.Container;
    private _fullWildSymbolWinView: PIXI.Container;
    private _scatterSymbolView: PIXI.Container;
    private _initialWinSymbolView: PIXI.Container;
    private _scatterWinView: PIXI.Container;
    private _allOutSymbols: TheCryptSymbol[] = [];
    private _collapsedReel: TheCryptSymbol[] = [];
    private _xWaysSymbol: TheCryptSymbol[] = [];
    private _isStacked: boolean = false;

    private readonly _reelId: number;

    constructor(id: number) {
        super();
        this._scatterWinView = new PIXI.Container();
        this._scatterSymbolView = new PIXI.Container();
        this._fullWildSymbolView = new PIXI.Container();
        this._fullWildSymbolWinView = new PIXI.Container();
        this._initialWinSymbolView = new PIXI.Container();

        this._scatterWinView.name = "scatterWinView" + id;
        this._scatterSymbolView.name = "scatterSymbol" + id;
        this._fullWildSymbolView.name = "fullWild" + id;
        this._fullWildSymbolWinView.name = "fullWildWin" + id;
        this._initialWinSymbolView.name = "initialWinSymbol" + id;

        this.name = "Reel " + id;
        this._isStacked = false;
        this._reelId = id;

        this.position = TheCryptGameConfig.reelPositions[id];
        this._scatterSymbolView.position = TheCryptGameConfig.reelPositions[id];
        this._fullWildSymbolView.position = TheCryptGameConfig.reelPositions[id];
        this._fullWildSymbolWinView.position = TheCryptGameConfig.reelPositions[id];
        this._scatterWinView.position = TheCryptGameConfig.reelPositions[id];
        this._initialWinSymbolView.position = TheCryptGameConfig.reelPositions[id];
        this._scatterSymbolView.sortableChildren = true;
        this._xWaysSymbol = [];
    }

    public removeAvalancheSymbol(symbol: TheCryptSymbol) {
        if (symbol.symName.indexOf("FW") > -1) {
            this._fullWildSymbolView.removeChild(symbol);
        } else if (symbol.symName === "S") {
            this.scatterSymbolView.removeChild(symbol);
        } else {
            this.removeChild(symbol);
        }
        symbol.remove();
    }

    private reArrangeSymbolLayer(): void {
        let normalSymbolIndex: number = 0;
        this._allOutSymbols.forEach((sym: TheCryptSymbol) => {
            if (!TheCryptHelper.isLargerSymbol(sym.symName)) {
                this.addChildAt(sym, normalSymbolIndex++);
            }
        });
    }

    private resetSymbolPositions(): void {
        if (TheCrypt.model.wildOnReel[this._reelId]) {
            return;
        }
        this._allOutSymbols.forEach((sym: TheCryptSymbol, i: number) => {
            sym.id = i;
            sym.position.set(0, TheCrypt.model.symbolPositionYsAfterReveal[this._reelId][i]);
        });
    }

    public switchSymbol(sym: TheCryptSymbol, toSym: string, isXWaySym: boolean = false): TimelineLite {
        const symSize: number = isXWaySym ? TheCrypt.model.mysterySymbolSize[this._reelId] : 1,
            currentM1Multiplier: number = TheCrypt.model.gameData.m1CurrentMultiplier;
        const newSyms: TheCryptSymbol[] = [];
        if (isXWaySym) {
            this._xWaysSymbol.push(sym);
        }
        for (let i: number = 0; i < symSize; i++) {
            const newSym: TheCryptSymbol = this.createSymbol({
                symName: toSym,
                reelSize: TheCrypt.model.afterRevealGameAreaSize[this._reelId],
                symSize: TheCrypt.model.afterRevealGameAreaSize[this._reelId] - 3,
                id: sym.id - i,
                reelId: this._reelId,
                M1Multiplier: (toSym === "M1" && currentM1Multiplier > 1) ? currentM1Multiplier : 1,
            }, TheCryptSymbolStates.NORMAL, sym.id);
            newSyms.push(newSym);
            newSym.alpha = 0;
        }

        const tl: TimelineLite = new TimelineLite();
        if (toSym === "FW") {
            tl.add((<TheCryptFullWild>newSyms[0]).getStateAnimation(TheCryptSymbolStates.EXPAND), 0);
        }
        tl.add(() => {
            //Replace xWays symbols
            this._allOutSymbols.splice(sym.id, 1);
            newSyms.forEach((toBeAddedSym: TheCryptSymbol) => {
                toBeAddedSym.alpha = 1;
                this._allOutSymbols.splice(sym.id, 0, toBeAddedSym);
            });

            //Remove extra X symbols
            while (this._allOutSymbols.length > 6) {
                const toBeDeletedSym: TheCryptSymbol = this._allOutSymbols.shift()!;
                this.removeAvalancheSymbol(toBeDeletedSym);
                if (!TheCryptHelper.isInvisibleSym(toBeDeletedSym.symName)) {
                    debugger;
                }
            }
            this.resetSymbolPositions();
            this.reArrangeSymbolLayer();
            if (!isXWaySym) {
                this.removeAvalancheSymbol(sym);
            }
        }, 0);

        return tl;
    }

    public removeAllSymbols(){
        while (this._allOutSymbols.length > 0) {
            const symbol = this._allOutSymbols.pop();
            if (symbol){
                this.removeAvalancheSymbol(symbol)
            }
        }
    }

    public initReel(symbols: ISymbolData[]): void {
        const toYs: number[] = TheCrypt.model.symbolPositionYs[this._reelId];
        let toBeFilledUpWithFullWild: boolean = false;
        for (let i: number = 0; i < symbols.length; i++) {
            const symbol: TheCryptSymbol = this.createSymbol(symbols[i], TheCryptSymbolStates.NORMAL, 0, true);
            symbol.position.set(0, symbol.symName === "FW" ? 300 : toYs[i]);
            if (symbol.symName === "FW") {
                toBeFilledUpWithFullWild = true;
                symbol.getStateAnimation(TheCryptSymbolStates.EXPAND);
            }

            if (TheCrypt.model.isRestore && symbol.symName[0] === "S") {
                ScatterCounter.allScatters.push(<TheCryptScatter>symbol);
            }

            if (!(TheCrypt.model.isRestore && (TheCryptHelper.isInvisibleSym(symbol.symName)))) {
                this._allOutSymbols.push(symbol);
            }
        }

        if (toBeFilledUpWithFullWild) {
            for (let j: number = 0; j < 5; j++) {
                const filledSymbol: TheCryptSymbol = this.createSymbol({
                    symName: "Y",
                    reelSize: 4,
                    symSize: 1,
                    id: j,
                    reelId: this._reelId
                }, TheCryptSymbolStates.NORMAL, 0, true);
                this._allOutSymbols.unshift(filledSymbol);
            }
        }
    }

    public createSymbol(symbolData: ISymbolData, state: TheCryptSymbolStates, stageIndex: number, init: boolean = false): TheCryptSymbol {
        let symbol: TheCryptSymbol;
        if (symbolData.symName.indexOf("FW") > -1) {
            symbol = new TheCryptFullWild(symbolData, state);
            (<TheCryptFullWild>symbol).showMultiplier(TheCrypt.model.data.xNudgeMultiplier[this._reelId]);
            this._fullWildSymbolView.addChild(symbol);
            symbol.position.set(0, 300);
            return symbol;
        }

        if (symbolData.symName === "S") {
            symbol = new TheCryptScatter(symbolData, state);
        } else {
            symbol = new TheCryptSymbol(symbolData, state);
        }

        if (init) {
            if (symbolData.symName === "S") {
                this._scatterSymbolView.addChild(symbol);
            } else if (symbolData.symName === "W" || symbolData.symName.indexOf("FW") > -1) {
                this._fullWildSymbolView.addChild(symbol);
            } else {
                this.addChild(symbol);
            }
        } else {
            this.addChild(symbol);
        }

        return symbol;
    }

    public dropOut(reelDelay: number): TimelineLite {
        const tl: gsap.core.Timeline = gsap.timeline({delay: reelDelay}),
            fastSpin: boolean = this.isFastSpin,
            subTimelines: gsap.core.Timeline[] = [];
        let counter: number = 0;
        while (this._allOutSymbols.length > 0) {
            const symbol: TheCryptSymbol = this._allOutSymbols.pop()!;
            TheCryptHelper.checkIfSymbolIsNull(symbol);
            const fromY: number = symbol.position.y,
                symSpinStopDelay: number = (fastSpin ? 0 : this.SYMBOLS_FALL_OUT_DELAY) * counter,
                turnToBlurDelay: number = (fastSpin ? 0 : this.SYMBOLS_FALL_OUT_DELAY) * counter,
                timeline = gsap.timeline({delay: symSpinStopDelay});

            timeline.add([
                () => {
                    if (TheCryptHelper.isLargerSymbol(symbol.symName)) {
                        symbol.parent.removeChild(symbol);
                        this.addChild(symbol);
                    }
                },
                gsap.to(symbol.position, {
                    duration: this.DURATION_SYMBOLS_FALL_OUT,
                    y: fromY - this.START_DROP_IN_POSITION,
                    ease: "sine.in"
                })
            ]);
            timeline.add([
                symbol.getStateAnimation(TheCryptSymbolStates.BLUR, this.DURATION_SYMBOLS_UNBLUR)
            ], (symbol.symName.indexOf("FW") > -1 || symbol.symName.indexOf("S") > -1) ? 0 : turnToBlurDelay);
            timeline.add(() => this.removeAvalancheSymbol(symbol));
            subTimelines.push(timeline);
            counter++;
        }


        if (subTimelines.length > 0) {
            subTimelines.push(gsap.timeline().add(() => {
                let scatterSymbolIndex: number = 0;
                this._allOutSymbols.forEach((sym: TheCryptSymbol, i: number) => {
                    if (sym.symName === "S") {
                        sym.parent.removeChild(sym);
                        this.scatterSymbolView.addChildAt(sym, scatterSymbolIndex++);
                    }
                });
            }));
        }
        if (this._reelId === 0) {
            tl.add(() => TheCryptSoundManager.playReelOut());
        }

        tl.add(subTimelines);
        return tl;
    }

    private symbolBounce(symbol: TheCryptSymbol, toY: number, newSymbol: boolean = true): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        if (newSymbol) {
            if (symbol.symName === "S") {
                tl.add(() => {
                    tl.add(ScatterCounter.addScatter(<TheCryptScatter>symbol), 0);
                }, 0);
            } else {
                tl.add([
                    () => {
                        if (symbol.symName === "W") {
                            TheCryptSoundManager.playWildLandingSound(this._reelId - 1);
                        } else if (symbol.symName === "M") {
                            TheCryptSoundManager.playXWayLandingSound(this._reelId);
                        }
                    },
                    symbol.getStateAnimation(TheCryptSymbolStates.LANDING)
                ], 0)
            }
        }
        tl.add(() => {
            if (TheCryptHelper.isLargerSymbol(symbol.symName)) {
                symbol.parent.removeChild(symbol);
                if (symbol.symName === "S") {
                    this._scatterWinView.addChild(symbol);
                } else if (symbol.symName === "W") {
                    this._fullWildSymbolWinView.addChild(symbol);
                } else if (symbol.symName === "M") {
                    this._scatterWinView.addChild(symbol);
                }
            }
        }, 0);

        const bounceDuration: number = 0.02 + symbol.id * 0.005;
        tl.to(symbol.position, bounceDuration, {
            y: toY - 5,
            ease: "power3.in"
        }, 0).to(symbol.position, 0.03 + symbol.id * 0.005, {
            y: toY,
            ease: "power3.out"
        }, bounceDuration);

        return tl;
    }

    private firstSymbolLandingOnReel(): void {
        if (TheCrypt.model.nearWinReels[this._reelId]) {
            TheCrypt.gameViewContainer.onNearWin();
            TheCryptSoundManager.playAnticipationLandingSound(this._reelId);
        } else {
            TheCryptSoundManager.playLandingSound(this._reelId);
        }
        TheCrypt.reelAnticipation.fadeOut(this._reelId);
    }

    public dropIn(delay: number, hasAnticipation: boolean): TimelineLite {
        const toYs: number[] = TheCrypt.model.symbolPositionYs[this._reelId],
            fastSpin: boolean = this.isFastSpin,
            tl: TimelineLite = new TimelineLite({delay: fastSpin ? 0 : delay}),
            reelData: ISymbolData[] = TheCrypt.model.reelsData[this._reelId];
        let timeline: gsap.core.Timeline,
            isFirstSymbol: boolean = true;
        const subTimelines: TimelineLite[] = [];
        this._allOutSymbols = [];
        this._isStacked = false;
        if (this._reelId === 0) {
            tl.add(() => TheCryptSoundManager.playReelIn());
        }

        for (let i: number = 0; i < 6; i++) {
            const symbol: TheCryptSymbol = this.createSymbol(reelData[i], TheCryptSymbolStates.BLUR, 0);
            this._allOutSymbols.push(symbol);
            if (!TheCryptHelper.isInvisibleSym(reelData[i].symName)) {
                symbol.position.y = this.START_DROP_IN_POSITION + symbol.id * symbol.symHeight;
                TheCryptHelper.checkIfSymbolIsNull(symbol);
                //stop spin fast if feature buy
                timeline = gsap.timeline({delay: ((fastSpin || TheCrypt.model.nearWinReels[this._reelId]) ? 0 : (this.SYMBOLS_FALL_IN_DELAY * (6 - i)))});
                const totalSpinDuration: number = fastSpin ? this.DURATION_FAST_SYMBOLS_FALL_SPIN : this.DURATION_SYMBOLS_FALL_IN;

                timeline.add([
                    gsap.to(symbol.position, {
                        duration: totalSpinDuration,
                        y: toYs[i],
                        ease: "power1.in",
                        delay: this.SYMBOLS_FALL_IN_DELAY
                    })
                ]);

                timeline.add([
                    () => {
                        if (isFirstSymbol) {
                            this.firstSymbolLandingOnReel();
                            isFirstSymbol = false;
                        }
                    },
                    this.symbolBounce(symbol, toYs[i])
                ]);
                subTimelines.push(timeline);
            }
        }
        tl.add(subTimelines);
        if (hasAnticipation) {
            tl.add(() => TheCrypt.reelAnticipation.update(this._reelId + 1));
        }
        tl.add(() => this.reArrangeSymbolLayer());
        return tl;
    }

    public fillUp(delay: number, hasAnticipation: boolean): TimelineLite {
        if (this._isStacked) {
            this._isStacked = false;
        }
        const toYs: number[] = TheCrypt.model.symbolPositionYs[this._reelId],
            temp: TheCryptSymbol[] = [];

        this._allOutSymbols.forEach((sym: TheCryptSymbol) => {
            if (!TheCryptHelper.isInvisibleSym(sym.symName)) {
                temp.push(sym);
            } else {
                this.removeAvalancheSymbol(sym);
            }
        });
        this._allOutSymbols = [];
        const fastSpin: boolean = this.isFastSpin;
        const fillUpTimeline = gsap.timeline({delay: fastSpin ? 0 : delay});
        let timeline: TimelineLite,
            isFirstSymbol: boolean = true,
            isTopSymbol: boolean = true;
        const subTimelines: TimelineLite[] = [],
            reelsFillUpData: ISymbolData[] = TheCrypt.model.reelsFillUpData[this._reelId],
            startIndex: number = (6 - temp.length - 1);

        for (let i: number = startIndex; i > -1; i--) {
            const symbol: TheCryptSymbol = this.createSymbol(reelsFillUpData[i], TheCryptSymbolStates.BLUR, temp.length > 0 ? (temp.length - 1) : 0);
            TheCryptHelper.checkIfSymbolIsNull(symbol);
            symbol.position.y = this.START_DROP_IN_POSITION + (symbol.id - startIndex) * symbol.symHeight;
            temp.unshift(symbol);
            timeline = gsap.timeline({delay: ((fastSpin || TheCrypt.model.nearWinReels[this._reelId]) ? 0 : (this.SYMBOLS_FALL_IN_DELAY * (startIndex - i)))});
            let delay: number = this.SYMBOLS_FALL_IN_DELAY;
            const fallDuration: number = fastSpin ? this.DURATION_FAST_SYMBOLS_FALL_SPIN : this.DURATION_SYMBOLS_FALL_IN;
            isTopSymbol = false;
            timeline.add([
                gsap.to(symbol.position, {
                    duration: fallDuration,
                    y: toYs[i],
                    ease: "power1.in",
                    delay: delay
                })
            ]);
            timeline.add(() => {
                if (isFirstSymbol) {
                    this.firstSymbolLandingOnReel();
                    isFirstSymbol = false;
                }
                this.symbolBounce(symbol, toYs[i]);
            });
            subTimelines.push(timeline);
        }

        this._allOutSymbols = temp;
        fillUpTimeline.add(subTimelines);
        if (hasAnticipation) {
            fillUpTimeline.add(() => TheCrypt.reelAnticipation.update(this._reelId + 1));
        }
        fillUpTimeline.add(() => this.reArrangeSymbolLayer());
        this.printAllSymbol("fillUp")
        return fillUpTimeline;
    }

    public calculateCollapseData(): void {
        const symbolsLeft: TheCryptSymbol[] = ArrayHelper.initArrayWithValues(this._allOutSymbols.length, (j: number) => {
            return this._allOutSymbols[j]
        });

        this._collapsedReel = [];

        for (let i: number = symbolsLeft.length - 1; i >= 0; i--) {
            if (!symbolsLeft[i].isRemoved) {
                this._collapsedReel.push(symbolsLeft[i]);
                symbolsLeft.splice(i, 1);
            } else {
                symbolsLeft.splice(i, 1);
            }
        }
    }

    public collapse(reelDelay: number): TimelineLite {
        const fastSpin: boolean = this.isFastSpin;
        const collapseTimeline: gsap.core.Timeline = gsap.timeline({delay: fastSpin ? 0 : reelDelay}),
            symNum: number = this._collapsedReel.length,
            subTimelines: gsap.core.Timeline[] = [];
        let timeline: gsap.core.Timeline,
            isFirstSymbol: boolean = true,
            toY: number = 4 * TheCryptGameConfig.symbolHeight;
        this._allOutSymbols = [];
        for (let i: number = 0; i < symNum; i++) {
            const symbol: TheCryptSymbol = this._collapsedReel[i];
            if (symbol == null) {
                debugger;
                throw new Error(`AvalancheReelPartAnimator.collapseAnimation, symbol is null!`);
            }

            if (!symbol.isRemoved) {
                this._allOutSymbols.push(symbol);
                const duration: number = (toY === symbol.position.y) ? 0 : ((fastSpin ? this.DURATION_FAST_SYMBOLS_COLLAPSE_FALL : this.DURATION_SYMBOLS_COLLAPSE_FALL) * (i + 1));
                timeline = gsap.timeline();
                if (duration > 0) {
                    toY -= symbol.symHeight * 0.5;
                    timeline.add([
                        gsap.to(symbol.position, {
                            duration: duration,
                            y: toY,
                            ease: "power2.in"
                        })
                    ]);
                    if (symbol.position.y != toY && !TheCryptHelper.isInvisibleSym(symbol.symName)) {
                        timeline.add([
                            () => {
                                if (isFirstSymbol) {
                                    TheCryptSoundManager.playLandingSound(this._reelId);
                                    isFirstSymbol = false;
                                }
                            },
                            this.symbolBounce(symbol, toY, false)
                        ]);
                    }
                    symbol.id = i;
                    toY -= symbol.symHeight * 0.5;
                }
                subTimelines.push(timeline);
            }
        }
        collapseTimeline.add(subTimelines);
        this._allOutSymbols.reverse();
        return collapseTimeline;
    }

    public nudgeAllSymbolsLeft(step: number, startIndex: number, stepDuration: number): TimelineLite {
        const nudgeTimeline: gsap.core.Timeline = gsap.timeline(),
            toYs: number[] = TheCrypt.model.symbolPositionYsAfterReveal[this._reelId];
        for (let i: number = 0; i <= startIndex; i++) {
            const symbol: TheCryptSymbol = this._allOutSymbols[i];
            if (!TheCryptHelper.isInvisibleSym(symbol.symName)) {
                nudgeTimeline.to(symbol.position, stepDuration, {
                    y: toYs[i + step],
                    ease: "power4.in"
                }, 0)
            }
        }
        return nudgeTimeline;
    }

    public nudge(steps: number, id: number): TimelineLite {
        if (this._isStacked) {
            return gsap.timeline();
        }
        const fastSpin: boolean = this.isFastSpin,
            hasNoAvalancheWin: boolean = TheCrypt.model.hasNoAvalancheWin,
            startIndex: number = TheCrypt.model.reels[this._reelId].indexOf("W"),
            symbol: TheCryptSymbol = this._allOutSymbols[startIndex],
            nudgeTimeline: gsap.core.Timeline = gsap.timeline();

        let stepDuration: number = 0.6,
            fastStepDuration: number = 0.2,
            delay: number = 0;
        nudgeTimeline.add([
            symbol.showNudgeMultiplier(stepDuration * 0.2, fastSpin || hasNoAvalancheWin || steps === 1, steps > 1)
        ], 0);
        delay += nudgeTimeline.duration();

        if (fastSpin || hasNoAvalancheWin || (this._reelId > TheCrypt.model.maxWinReelId)) {
            nudgeTimeline.add([
                () => TheCryptSoundManager.playSound("wildNudgeFast"),
                symbol.upgradeWildMultiplier(steps, 0, 0, steps > 1),
                this.nudgeAllSymbolsLeft(steps - 1, startIndex, fastStepDuration)
            ], delay);
            for (let s: number = (startIndex + 1); s < 6; s++) {
                nudgeTimeline.add(this._allOutSymbols[s].breakOnNudge(fastStepDuration), delay);
            }
            nudgeTimeline.add(() => TheCrypt.gameViewContainer.onWildNudge(), delay + fastStepDuration * 0.5);
            delay += fastStepDuration;
        } else if (steps === 1) {
            nudgeTimeline.add(() => {
                TheCrypt.gameViewContainer.onWildNudge();
                TheCryptSoundManager.playSound(TheCryptSoundConfig.WILD_NUDGE_FINAL[id]);
            }, delay);
        } else {
            for (let i: number = 1; i < steps; i++) {
                TheCryptHelper.checkIfSymbolIsNull(symbol);
                nudgeTimeline.add([
                    symbol.upgradeWildMultiplier(i + 1, stepDuration * 0.8, stepDuration * 0.2),
                    this.nudgeAllSymbolsLeft(i, startIndex, stepDuration)
                ], delay);
                if ((i + startIndex) < 6) {
                    nudgeTimeline.add(this._allOutSymbols[i + startIndex].breakOnNudge(stepDuration), delay);
                }
                nudgeTimeline.add(() => {
                    TheCrypt.gameViewContainer.onWildNudge();
                    TheCryptSoundManager.playNudgeSound(((i + 1) === steps) ? id : -1, i);
                    // symbol.wildMultiplier = "X" + (i + 1).toString();
                }, delay);
                delay += stepDuration;
            }
        }
        nudgeTimeline.add([
            TheCrypt.reelFrame.onWildExpand(),
            this.switchSymbol(symbol, "FW")
        ], delay);
        nudgeTimeline.add(() => {
            this._allOutSymbols.forEach((symOnReel: TheCryptSymbol, id: number) => {
                if (id != startIndex) {
                    symOnReel.visible = false;
                }
            });
        });
        this._isStacked = true;
        return nudgeTimeline;
    }

    public expand(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            reelSize: number = TheCrypt.model.afterRevealGameAreaSize[this._reelId];

        const symExpandDuration: number = this.DURATION_SYMBOLS_EXPAND;
        let toY: number = 0,
            xWaySymbol: TheCryptSymbol;
        for (let i: number = 0; i < 6; i++) {
            const symbol: TheCryptSymbol = this._allOutSymbols[i];
            TheCryptHelper.checkIfSymbolIsNull(symbol);

            if (!TheCryptHelper.isInvisibleSym(symbol.symName)) {
                const mysterySymbolSize: number = (symbol.isXWay ? TheCrypt.model.mysterySymbolSize[this._reelId] : 1),
                    symHeight: number = (TheCryptGameConfig.reelHeight / reelSize) * mysterySymbolSize,
                    scale = MathHelper.roundToDecimals(symHeight / symbol.symHeight, 2);
                toY += symHeight * 0.5;
                if (symbol.isXWay) {
                    xWaySymbol = symbol;
                }
                tl.add([
                    () => {
                        symbol.changeHeight(symExpandDuration, reelSize, scale)
                    },
                    gsap.to(symbol.position, {
                        duration: symExpandDuration,
                        y: toY
                    })], 0);
                toY += symHeight * 0.5;
            }
        }
        tl.add(() => {
            let totalWays: number = 1;
            for (let i: number = 0; i < 6; i++) {
                totalWays *= ((i <= this._reelId) ? TheCrypt.model.afterRevealGameAreaSize[i] : 4);
            }
            TheCrypt.reelFrame.updateWays(totalWays);
        }, 0);

        TheCryptHelper.checkIfSymbolIsNull(xWaySymbol!);
        tl.add(() => {
            const reelSize: number = TheCrypt.model.afterRevealGameAreaSize[this._reelId];
            for (let i: number = 0; i < 6; i++) {
                const symbol: TheCryptSymbol = this._allOutSymbols[i];
                if (!TheCryptHelper.isInvisibleSym(symbol.symName) && !symbol.isXWay) {
                    symbol.reelSize = reelSize;
                }
            }
        }, symExpandDuration-0.1);

        tl.add(this.switchSymbol(xWaySymbol!, TheCrypt.model.mysterySymbolRevealed, true), symExpandDuration);
        if (TheCrypt.autoPlayPlugin.isAutoplayRound && TheCrypt.model.hasNoAvalancheWin) {
            tl.add(() => {
            }, "+=0.5");
        }

        return tl;
    }

    private printAllSymbol(meg: string): void {
        return;
        const temp: string[] = [];
        this._allOutSymbols.forEach((sym: TheCryptSymbol) => {
            temp.push(sym.symName);
        });
        //console.log(`${this._reelId} ${meg} ${temp}`);
    }

    public getSymbol(position: number): TheCryptSymbol {
        this.printAllSymbol("get symbol");
        if (position > (this._allOutSymbols.length - 1)) {
            debugger;
        }
        return this._allOutSymbols[position];
    }

    public getAllSymbols(): TheCryptSymbol[] {
        return this._allOutSymbols;
    }

    get scatterSymbolView(): PIXI.Container {
        return this._scatterSymbolView;
    }

    get fullWildSymbolView(): PIXI.Container {
        return this._fullWildSymbolView;
    }

    get normalView(): PIXI.Container {
        return this;
    }

    get initialWinSymbolView(): PIXI.Container {
        return this._initialWinSymbolView;
    }

    get scatterWinView(): PIXI.Container {
        return this._scatterWinView;
    }

    get fullWildSymbolWinView(): PIXI.Container {
        return this._fullWildSymbolWinView;
    }

    get isFastSpin(): boolean {
        const fastSpin: boolean = TheCrypt.apiPlugin.settings.get(APISetting.FAST_SPIN, false);
        return fastSpin;
    }

    get isStacked(): boolean {
        return this._isStacked;
    }

    get needFillUp(): boolean {
        return (this._allOutSymbols.length < 6);
    }

    get needCollapse(): boolean {
        return (this._collapsedReel.length != this._allOutSymbols.length);
    }
}